/**
 * Enhances the cascade dropdown menu (block setting: menu_layout = "dropdown").
 *
 * Level-1 open/close is handled by the parent <header-menu> component and CSS
 * (hover / focus-within). This component only adds behaviour that CSS cannot:
 *
 *   1. Flyout edge detection — flips a level-2 panel to the opposite side when
 *      it would overflow the viewport.
 *   2. Keyboard support for the flyout triggers — ArrowRight to open,
 *      Escape / ArrowLeft to close.
 *   3. Keeping aria-expanded on each flyout trigger in sync with the actual
 *      open state, for pointer and keyboard alike.
 *
 * It is intentionally independent of the mega-menu height/clip animation logic
 * in header-menu.js.
 */
class HeaderDropdown extends HTMLElement {
  connectedCallback() {
    this.addEventListener('pointerover', this.#handlePointerOver);
    this.addEventListener('pointerout', this.#handlePointerOut);
    this.addEventListener('focusin', this.#handleFlyoutFocus);
    this.addEventListener('focusout', this.#handleFlyoutBlur);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerover', this.#handlePointerOver);
    this.removeEventListener('pointerout', this.#handlePointerOut);
    this.removeEventListener('focusin', this.#handleFlyoutFocus);
    this.removeEventListener('focusout', this.#handleFlyoutBlur);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerOver = (event) => {
    const item = this.#closestFlyoutItem(event.target);
    if (item) {
      this.#setExpanded(item, true);
      this.#positionFlyout(item);
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerOut = (event) => {
    const item = this.#closestFlyoutItem(event.target);
    // Ignore moves that stay inside the same flyout item
    if (item && !item.contains(/** @type {Node | null} */ (event.relatedTarget))) {
      this.#setExpanded(item, false);
    }
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFlyoutFocus = (event) => {
    const item = this.#closestFlyoutItem(event.target);
    if (item) {
      this.#setExpanded(item, true);
      this.#positionFlyout(item);
    }
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFlyoutBlur = (event) => {
    const item = this.#closestFlyoutItem(event.target);
    // Collapse only when focus leaves the flyout item entirely
    if (item && !item.contains(/** @type {Node | null} */ (event.relatedTarget))) {
      this.#setExpanded(item, false);
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const item = this.#closestFlyoutItem(target);

    switch (event.key) {
      case 'Escape': {
        const openItem = item ?? this.querySelector('.header-dropdown__item--has-flyout [aria-expanded="true"]')?.closest('.header-dropdown__item--has-flyout');
        if (openItem instanceof HTMLElement) {
          this.#setExpanded(openItem, false);
          openItem.querySelector('.header-dropdown__link')?.focus();
        }
        break;
      }
      case 'ArrowRight': {
        if (item) {
          event.preventDefault();
          this.#setExpanded(item, true);
          this.#positionFlyout(item);
          item.querySelector('.header-dropdown__flyout .header-dropdown__link')?.focus();
        }
        break;
      }
      case 'ArrowLeft': {
        const flyout = target.closest('.header-dropdown__flyout');
        const parentItem = flyout?.closest('.header-dropdown__item--has-flyout');
        if (parentItem instanceof HTMLElement) {
          event.preventDefault();
          this.#setExpanded(parentItem, false);
          parentItem.querySelector('.header-dropdown__link')?.focus();
        }
        break;
      }
    }
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #closestFlyoutItem(target) {
    if (!(target instanceof Element)) return null;
    const item = target.closest('.header-dropdown__item--has-flyout');
    return item instanceof HTMLElement ? item : null;
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setExpanded(item, expanded) {
    const link = item.querySelector(':scope > .header-dropdown__link');
    link?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * Decide which side the flyout should open on so it stays within the viewport.
   * @param {HTMLElement} item
   */
  #positionFlyout(item) {
    const flyout = /** @type {HTMLElement | null} */ (item.querySelector(':scope > .header-dropdown__flyout'));
    if (!flyout) return;

    // Reset to default (end side) before measuring.
    flyout.removeAttribute('data-flyout-side');

    const rect = flyout.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      flyout.dataset.flyoutSide = 'start';
    }
  }
}

if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', HeaderDropdown);
}
